import { useInspectorStore } from '@/stores/inspectorStore';
import type { IGroup } from '@/types/filterBuilderTypes';
import HistoryInspectorContent from '@/components/HistoryInspector/HistoryInspectorContent.vue';

/**
 * Entry-point helpers for the History Inspector (SAU-138).
 *
 * The inspector lists `LiveTrackAction` records via the existing ES search
 * endpoint, context-seeded by the caller:
 *   - user history: scoped to a user (a dropdown lets you switch users,
 *     defaulting to whoever was passed in — usually the current user),
 *   - track history: scoped to one live track (from a drill-down or the
 *     per-track button).
 */

/** Backend resource slug — drives the `POST /api/{slug}/search` endpoint. */
export const HISTORY_ENTITY_SLUG = 'live_track_action';

/**
 * Build a seed scope: a single equals-rule on `column`, plus a rule excluding
 * soft-deleted actions (`deleted_at` set — `is_empty_or_null` takes no value
 * and maps to a `must_not exists` clause in the ES query builder).
 *
 * Rule ids are fixed, not random: the same seed then produces a structurally
 * identical scope, so vue-query serves a cache hit when the inspector re-opens
 * or re-drills into a view it has already loaded.
 */
function buildSeedScope(equalsRuleId: string, column: string, value: number): IGroup {
  return {
    id: 'root',
    scopeType: 'and',
    rules: [
      { id: equalsRuleId, column, criterion: 'equals', value, field: null },
      {
        id: 'exclude-deleted',
        column: 'deleted_at',
        criterion: 'is_empty_or_null',
        value: null,
        field: null,
      },
    ],
  };
}

/** Seed scope: every (non-deleted) action performed by a given user. */
export function scopeForUser(userId: number): IGroup {
  return buildSeedScope('performed-by-user-id', 'performed_by_user_id', userId);
}

/** Seed scope: every (non-deleted) action on a given live track. */
export function scopeForTrack(liveTrackId: number): IGroup {
  return buildSeedScope('live-track-id', 'live_track_id', liveTrackId);
}

export function useHistoryInspector() {
  /**
   * Open the inspector as a fresh root view in user-history mode, defaulting
   * the user dropdown to `userId` (the caller passes the current user).
   */
  function openUserHistory(userId: number): void {
    useInspectorStore().open('User History', HistoryInspectorContent, {
      seedKind: 'user',
      initialUserId: userId,
    });
  }

  /**
   * Open the inspector seeded to one track's action history.
   * `push: true` stacks it on top of the current view (drill-down) so the
   * back chevron returns to the prior view.
   */
  function openTrackHistory(
    liveTrackId: number,
    title: string,
    options: { push?: boolean } = {},
  ): void {
    const inspectorStore = useInspectorStore();
    const props = { seedKind: 'track' as const, initialFilter: scopeForTrack(liveTrackId) };
    if (options.push) {
      inspectorStore.push(title, HistoryInspectorContent, props);
    } else {
      inspectorStore.open(title, HistoryInspectorContent, props);
    }
  }

  return { openUserHistory, openTrackHistory };
}
